// Firebase for the web app. The config comes from Firebase Hosting at
// /__/firebase/init.json, so the same build runs on dev and on prod.
// Nothing here writes. Training writes go through server code (callServer).

import { progressFrom, type StationProgress } from "./field-demo";
import type { LotoRow } from "./training-view";
import { initializeApp, type FirebaseApp } from "firebase/app";
import { requestRowOf, type WorkRequestRow } from "./work-requests";
import { nearMissRowOf, type NearMissRow } from "./near-miss";
import { visitRowOf, type VisitRow } from "./contractor";
import { eewpSiteRows, qewEmailsOf, type EewpSiteRow } from "./eewp";
import {
  getAuth,
  onAuthStateChanged,
  signInWithCustomToken,
  signInWithEmailAndPassword,
  signOut,
  type Auth,
  type User,
} from "firebase/auth";
import {
  collection,
  doc,
  getCountFromServer,
  getDoc,
  getDocs,
  getDocFromCache,
  getDocFromServer,
  getDocsFromCache,
  getDocsFromServer,
  initializeFirestore,
  onSnapshot,
  CACHE_SIZE_UNLIMITED,
  limit,
  orderBy,
  persistentLocalCache,
  persistentMultipleTabManager,
  query,
  setDoc,
  where,
  type Firestore,
} from "firebase/firestore";
import { getFunctions, httpsCallable, type Functions } from "firebase/functions";
import { getDownloadURL, getStorage, ref as storageRef, uploadBytes } from "firebase/storage";
import type { Badge, SignInClaims } from "./badge";
import type { Quiz, QuizAttempt, QuizQuestion, TrainingRecord } from "./training";
import type { AssetDoc } from "./guest-asset";
import type { PackAsset, SitePack } from "./survey";
import type { WalkStatusDoc } from "./outbox";
import type { AssetRow, Contact, Site } from "./sites";
import type { BadgeDoc, Person } from "./people";
import type { ProgramContact, ProgramSettings } from "./settings";
import { withPrefixList, type EquipmentClass, type FieldValue, type Plant } from "./lists";
import type { FieldChange, StatusChange, Task } from "./tasks";
import type { SopDoc } from "./resources";
import { incomingPath, safeFileName, type CopyLine, type HistoryEntry, type Reference } from "./references";
import type { GloveDoc, InspectionDoc } from "./gloves-roster";
import { LOG_LIMIT, emailRowOf, type EmailRow } from "./email";
import type { FailedInSession } from "./training-server";
import type { AttemptScore } from "./training-view";

// Two sign-in slots in one browser (fix 2026-10-10, Robert found admin and QEW tabs flipping each other).
// * "admin": the email sign-in on the admin pages. The default app, so an admin already signed in stays signed in.
// * "field": the QEW bag-tag sign-in. Its own app, so its saved sign-in sits beside the admin one, never over it.
// Every QEW tab still shares the one field sign-in: one phone, one QEW (approved 2026-10-06).
// The page picks its slot at start (useSlot). Server calls go out under that slot's sign-in.
export type Slot = "admin" | "field";

let app: FirebaseApp | null = null;
let db: Firestore | null = null;
const auths: Partial<Record<Slot, Auth>> = {};
const fnsBySlot: Partial<Record<Slot, Functions>> = {};
let active: Slot = "admin";

export async function startFirebase(): Promise<void> {
  if (app) return;
  const res = await fetch("/__/firebase/init.json");
  if (!res.ok) throw new Error("Firebase settings not found. Open the app from its web address.");
  const config = await res.json();
  app = initializeApp(config);
  const field = initializeApp(config, "field");
  auths.admin = getAuth(app);
  auths.field = getAuth(field);
  // On-device store (IEA_OFFLINE_SHELL_WORDS.md section 10): survey saves wait on the iPad and
  // send at signal. With signal, reads still come from the server as before.
  // Unlimited: a downloaded site pack is never pushed out of the iPad's store to make room.
  db = initializeFirestore(app, { localCache: persistentLocalCache({ tabManager: persistentMultipleTabManager(), cacheSizeBytes: CACHE_SIZE_UNLIMITED }) });
  fnsBySlot.admin = getFunctions(app, "us-central1");
  fnsBySlot.field = getFunctions(field, "us-central1");
}

/** The slot this page works in. Admin pages: "admin". Guest, tag, QEW and quiz pages: "field". */
export function useSlot(slot: Slot): void {
  active = slot;
}

function needAuth(slot: Slot = active): Auth {
  const a = auths[slot];
  if (!a) throw new Error("Firebase not started");
  return a;
}

function needDb(): Firestore {
  if (!db) throw new Error("Firebase not started");
  return db;
}

/** The admin sign-in. Admin pages only. */
export function watchSignIn(cb: (user: User | null) => void): void {
  onAuthStateChanged(needAuth("admin"), cb);
}

/** Admin email sign-in. Always the admin slot. */
export async function signIn(email: string, password: string): Promise<void> {
  await signInWithEmailAndPassword(needAuth("admin"), email.trim(), password);
}

/** QEW sign-in: the token comes from the qewSignIn server step. Always the field slot. */
export async function signInWithBadgeToken(token: string): Promise<void> {
  await signInWithCustomToken(needAuth("field"), token);
}

/** The badge on a slot's sign-in, once the saved sign-in has loaded. Null when signed out. */
export async function currentBadge(slot: Slot = active): Promise<SignInClaims | null> {
  const a = needAuth(slot);
  await a.authStateReady();
  return a.currentUser ? badgeOf(a.currentUser) : null;
}

/** Signs out one slot only. The other slot keeps its sign-in. */
export async function signOutNow(slot: Slot = active): Promise<void> {
  await signOut(needAuth(slot));
}

/** The badge copy on the sign-in, or null when the login carries none. */
export async function badgeOf(user: User): Promise<SignInClaims | null> {
  // With signal: a fresh check, so a badge change shows at once. No signal: the copy on the
  // device (IEA_OFFLINE_SHELL_WORDS.md section 4). The rules still check the badge on every save.
  const token = await user.getIdTokenResult(navigator.onLine !== false).catch(() => user.getIdTokenResult(false));
  const c = token.claims as Record<string, unknown>;
  if (typeof c.company !== "string" || typeof c.badgeId !== "string") return null;
  if (!Array.isArray(c.sites) || !Array.isArray(c.authorities)) return null;
  return {
    company: c.company,
    badgeId: c.badgeId,
    holderName: typeof c.holderName === "string" ? c.holderName : c.badgeId,
    sites: c.sites as string[],
    authorities: c.authorities as SignInClaims["authorities"],
  };
}

async function readAll<T>(company: string, name: string): Promise<T[]> {
  const snap = await getDocs(collection(needDb(), "companies", company, name));
  return snap.docs.map((d) => d.data() as T);
}

export interface TrainingData {
  quizzes: Quiz[];
  questions: QuizQuestion[];
  records: TrainingRecord[];
  /** QEW tags, so a trainee shows on the roster before the first node closes. */
  qewTags: { badgeId: string; name: string }[];
  /** Scores live on the attempts. Admin reads all (rule 9). */
  attempts: AttemptScore[];
  /** Node 4 demo stations by QEW ID, from people records. Admin reads people (rule 16). */
  stations: Record<string, StationProgress>;
  /** LOTO by QEW ID, from people records, for the trainee page LOTO row (Stage 4b). */
  loto: Record<string, LotoRow>;
}

/** Admin read of everything the two pages show. About 150 docs. */
export async function loadTraining(company: string): Promise<TrainingData> {
  const [quizzes, questions, records, badges, rawAttempts, people] = await Promise.all([
    readAll<Quiz>(company, "quizzes"),
    readAll<QuizQuestion>(company, "quizQuestions"),
    readAll<TrainingRecord>(company, "trainingRecords"),
    readAll<Badge>(company, "badges"),
    readAll<QuizAttempt>(company, "quizAttempts"),
    getDocs(collection(needDb(), "companies", company, "people")),
  ]);
  const stations: Record<string, StationProgress> = {};
  const loto: Record<string, LotoRow> = {};
  const str = (v: unknown) => (typeof v === "string" && v ? v : null);
  for (const p of people.docs) {
    const got = progressFrom(p.get("fieldDemoStations"));
    if (Object.keys(got).length) stations[p.id] = got;
    loto[p.id] = {
      reportedOn: p.get("lotoNoneReported") === true ? null : str(p.get("lotoTrainedOn")),
      verifiedOn: str(p.get("lotoVerifiedOn")) ?? str(p.get("lotoLastVerified.verifiedOn")),
      verifiedByName: str(p.get("lotoVerifiedByName")) ?? str(p.get("lotoVerifiedBy")),
    };
  }
  const attempts: AttemptScore[] = rawAttempts.map((a) => ({
    attemptId: a.attemptId,
    qewId: a.qewId,
    quizId: a.quizId,
    sessionId: a.sessionId ?? "",
    attemptNumber: a.attemptNumber,
    scorePct: a.scorePct,
    questionsCorrect: a.questionsCorrect,
    questionsTotal: a.questionsTotal,
    isPassed: a.isPassed,
    correctedScorePct: a.correctedScorePct ?? null,
    takenAt: a.takenAt,
  }));
  const qewTags = badges
    .filter((b) => b["Badge Type"] === "QEW" && b.Status === "Active")
    .map((b) => ({ badgeId: b["Badge ID"], name: b["Holder Name"] }));
  return { quizzes, questions, records, qewTags, attempts, stations, loto };
}

/** Stored at companies/{company}/sites/{site}/trainingSessions/{sessionId}. Written by server code. */
export interface TrainingSession {
  sessionId: string;
  sessionCode: string;
  siteNumber: string;
  status: "open" | "ended";
  startedAt: string;
  startedByName: string;
  endedAt: string | null;
  endedByName: string | null;
  endedReason?: string | null;
  reopens?: { reason: string; context: string; byName: string; at: string }[];
}

export interface SiteSessions {
  open: TrainingSession | null;
  /** Last five ended, newest first. */
  past: TrainingSession[];
}

/** The open session and the last five for one site. Admin covering the site (rule 10). */
export async function loadSessions(company: string, site: string): Promise<SiteSessions> {
  const col = collection(needDb(), "companies", company, "sites", site, "trainingSessions");
  const [openSnap, recentSnap] = await Promise.all([
    getDocs(query(col, where("status", "==", "open"), limit(1))),
    getDocs(query(col, orderBy("startedAt", "desc"), limit(6))),
  ]);
  const open = (openSnap.docs[0]?.data() as TrainingSession | undefined) ?? null;
  const past = recentSnap.docs
    .map((d) => d.data() as TrainingSession)
    .filter((x) => x.status !== "open")
    .slice(0, 5);
  return { open, past };
}

/** Failed attempts in a session, for the End Session review gate. Admin reads all attempts (rule 9). */
export async function failedInSession(company: string, sessionId: string): Promise<FailedInSession[]> {
  const snap = await getDocs(
    query(collection(needDb(), "companies", company, "quizAttempts"), where("sessionId", "==", sessionId), where("isPassed", "==", false)),
  );
  return snap.docs.map((d) => {
    const a = d.data() as QuizAttempt;
    return { attemptId: d.id, name: a.name, quizId: a.quizId, scorePct: a.scorePct, correctionFinished: Boolean(a.correctedAt) };
  });
}

// ---- Sites page (SITES_PAGE_WORDS.md) ----

export interface SitesData {
  sites: Site[];
  /** Asset count per site number, counted on the server. */
  assetCounts: Record<string, number>;
  /** Program and site contacts. Admin reads (rule 13). */
  contacts: Contact[];
  /** The Demo set, keyed mcc, msd, panel. Admin reads (rule 14). */
  demo: Record<string, AssetDoc & { assetIdPattern?: string }>;
}

/** Sites, their asset counts, contacts and the Demo set. About 10 reads plus 5 counts. */
export async function loadSitesData(company: string): Promise<SitesData> {
  const d = needDb();
  const [siteSnap, contactSnap, demoSnap] = await Promise.all([
    getDocs(collection(d, "companies", company, "sites")),
    getDocs(collection(d, "companies", company, "contacts")),
    getDocs(collection(d, "companies", company, "demoAssets")),
  ]);
  const sites = siteSnap.docs.map((x) => ({ ...(x.data() as Site), siteNumber: x.id }));
  const counts = await Promise.all(
    sites.map(async (s) => [s.siteNumber, (await getCountFromServer(collection(d, "companies", company, "sites", s.siteNumber, "assets"))).data().count] as const),
  );
  const demo: SitesData["demo"] = {};
  for (const x of demoSnap.docs) demo[x.id] = x.data() as SitesData["demo"][string];
  return {
    sites,
    assetCounts: Object.fromEntries(counts),
    contacts: contactSnap.docs.map((x) => ({ ...(x.data() as Contact), contactId: x.id })),
    demo,
  };
}

/** Every asset at one site, for the read-only list. Covered site (rules 2 and 3). */
export async function loadSiteAssets(company: string, site: string): Promise<AssetRow[]> {
  const snap = await getDocs(collection(needDb(), "companies", company, "sites", site, "assets"));
  return snap.docs.map((x) => {
    const a = x.data() as AssetRow;
    return { assetId: x.id, location: a.location ?? "", equipmentType: a.equipmentType ?? "", isRemoved: a.isRemoved === true };
  });
}

// ---- People & Badges (PEOPLE_BADGES_WORDS.md) ----

export interface PeopleData {
  /** Every badge in the company: the five sites' tags plus the admin badge. Admin reads (rule 5). */
  badges: BadgeDoc[];
  /** Every person record. Admin reads (rule 16). */
  people: Person[];
  /** Site number to name, for headings. */
  siteNames: Record<string, string>;
  siteActive: Record<string, boolean>;
  /** Node records, to work out Nodes 1 to 3 done. Admin reads (rule 9). */
  records: { qewId: string; nodeId: string }[];
}

/** About 520 docs: 495 tags, 22 people, 5 sites, and the node records. */
export async function loadPeopleData(company: string): Promise<PeopleData> {
  const d = needDb();
  const [badgeSnap, peopleSnap, siteSnap, recordSnap] = await Promise.all([
    getDocs(collection(d, "companies", company, "badges")),
    getDocs(collection(d, "companies", company, "people")),
    getDocs(collection(d, "companies", company, "sites")),
    getDocs(collection(d, "companies", company, "trainingRecords")),
  ]);
  const siteNames: Record<string, string> = {};
  const siteActive: Record<string, boolean> = {};
  for (const x of siteSnap.docs) {
    siteNames[x.id] = String(x.get("name") ?? `Site ${x.id}`);
    siteActive[x.id] = x.get("isActive") === true;
  }
  return {
    badges: badgeSnap.docs.map((x) => ({ ...(x.data() as BadgeDoc), "Badge ID": x.id })),
    people: peopleSnap.docs.map((x) => {
      const p = x.data() as Person & { lotoLastVerified?: { verifiedOn?: string } };
      return { ...p, qewId: x.id, siteNumber: p.siteNumber ?? x.id.slice(0, 2), lotoLastVerifiedOn: p.lotoLastVerified?.verifiedOn ?? null };
    }),
    siteNames,
    siteActive,
    records: recordSnap.docs.map((x) => ({ qewId: String(x.get("qewId") ?? ""), nodeId: String(x.get("nodeId") ?? "") })),
  };
}

// ---- Program Settings (PROGRAM_SETTINGS_WORDS.md) ----

export interface SettingsData {
  settings: ProgramSettings;
  programContacts: ProgramContact[];
  /** Active site contacts with the LOTO Verifier tick, and with LOTO Awareness. */
  lotoVerifierCount: number;
  lotoAwarenessCount: number;
  /** EEWP approvals by site (EEWP_WORDS.md rev 2). */
  eewpSites: EewpSiteRow[];
  /** settings/drive (rule 33), for the Google Drive menu line. Null: never set up. */
  drive?: Partial<import("./drive-folders").DriveSettings> | null;
}

/** settings/program (rule 17) and every contact (rule 13). */
export async function loadSettingsData(company: string): Promise<SettingsData> {
  const d = needDb();
  // Read the one document. Rule 17 opens settings/program only, so listing the whole
  // settings collection is refused (the "missing or insufficient permissions" bug).
  const [program, contactSnap, siteSnap, peopleSnap] = await Promise.all([
    getDoc(doc(d, "companies", company, "settings", "program")),
    getDocs(collection(d, "companies", company, "contacts")),
    getDocs(collection(d, "companies", company, "sites")),
    getDocs(collection(d, "companies", company, "people")),
  ]);
  if (!program.exists()) throw new Error("Program settings not found. Run the config copy first.");
  const contacts: Record<string, unknown>[] = contactSnap.docs.map((x) => ({ ...x.data(), contactId: x.id }));
  const site = contacts.filter((c) => c.scope === "site" && c.isActive === true);
  const ticked = (key: string) => site.filter((c) => (c.usedFor as Record<string, unknown> | undefined)?.[key] === true).length;
  const drive = await loadDriveSettings(company);
  return {
    drive,
    settings: program.data() as ProgramSettings,
    programContacts: contacts.filter((c) => c.scope === "program") as unknown as ProgramContact[],
    lotoVerifierCount: ticked("lotoVerifier"),
    lotoAwarenessCount: ticked("lotoAwareness"),
    eewpSites: eewpSiteRows(
      siteSnap.docs.map((x) => ({ ...(x.data() as Site), siteNumber: x.id })),
      contacts as unknown as Contact[],
      qewEmailsOf(peopleSnap.docs.map((x) => x.data())),
    ),
  };
}

// ---- Lists (LISTS_PAGE_WORDS.md) ----

export interface ListsData {
  classes: EquipmentClass[];
  values: FieldValue[];
  plants: Plant[];
}

/** The three lists (rule 18): about 30 classes, 220 values, 25 plants. */
export async function loadListsData(company: string): Promise<ListsData> {
  const d = needDb();
  const items = (list: string) => getDocs(collection(d, "companies", company, "lists", list, "items"));
  const [c, v, p] = await Promise.all([items("equipmentClasses"), items("fieldLists"), items("plants")]);
  return {
    classes: c.docs.map((x) => ({ ...(x.data() as EquipmentClass), code: x.id, alsoAccepts: (x.get("alsoAccepts") as string[] | undefined) ?? [], devices: (x.get("devices") as string[] | undefined) ?? [] })),
    // Model Tag Prefixes shows today's defaults until the first save writes them (src/lists.ts).
    values: withPrefixList(v.docs.map((x) => ({ ...(x.data() as FieldValue), id: x.id, group: String(x.get("group") ?? ""), notes: String(x.get("notes") ?? "") }))),
    plants: p.docs.map((x) => ({ ...(x.data() as Plant), siteNumber: x.id })),
  };
}

// ---- Tasks (TASKS_PAGE_WORDS.md) ----

export interface TasksData {
  tasks: Task[];
  /** On / Off flips per task. */
  changes: Record<string, StatusChange[]>;
  /** Tier and Task Type changes per task. */
  edits: Record<string, FieldChange[]>;
  /** The SOP Library, for Open SOP. */
  sops: SopDoc[];
}

/** Every task with its flips (rule 20), and the SOP Library (rule 21). */
export async function loadTasksData(company: string): Promise<TasksData> {
  const d = needDb();
  const [t, s] = await Promise.all([getDocs(collection(d, "companies", company, "tasks")), getDocs(collection(d, "companies", company, "sops"))]);
  const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));
  const tasks: Task[] = t.docs.map((x) => ({
    taskId: x.id,
    name: str(x.get("name")) || x.id,
    tier: Number(x.get("tier")),
    taskType: str(x.get("taskType")),
    isActive: x.get("isActive") === true,
    sopNumber: str(x.get("sopNumber")),
    likelihood1305C: str(x.get("likelihood1305C")),
    authorizationPathway: str(x.get("authorizationPathway")),
    lastDeactivatedOn: str(x.get("lastDeactivatedOn")),
    lastReactivatedOn: str(x.get("lastReactivatedOn")),
  }));
  const flips = await Promise.all(t.docs.map((x) => getDocs(collection(d, "companies", company, "tasks", x.id, "statusChanges"))));
  const changes: Record<string, StatusChange[]> = {};
  const edits: Record<string, FieldChange[]> = {};
  t.docs.forEach((x, i) => {
    changes[x.id] = flips[i]!.docs.map((c) => ({
      changedOn: str(c.get("changedOn")),
      changedBy: str(c.get("changedBy")),
      changedByName: str(c.get("changedByName")),
      isActive: c.get("isActive") === true,
      reason: str(c.get("reason")),
      source: str(c.get("source")),
    }));
    edits[x.id] = ((x.get("editHistory") as FieldChange[] | undefined) ?? []).filter((e) => e && (e.field === "tier" || e.field === "taskType"));
  });
  const sops: SopDoc[] = s.docs.map((x) => ({
    sopNumber: x.id,
    title: str(x.get("title")) || x.id,
    fileUrl: str(x.get("fileUrl")),
    version: str(x.get("version")),
    minReadSeconds: Number(x.get("minReadSeconds") ?? 30),
    requiresFullAck: x.get("requiresFullAck") !== false,
    isPermit: x.get("isPermit") === true,
  }));
  return { tasks, changes, edits, sops };
}

// ---- Reference Library (REFERENCE_LIBRARY_WORDS.md) ----

export interface ReferencesData {
  refs: Reference[];
  copy: CopyLine | null;
}

/** Every library record (rule 22) and the copy line the copy job writes. */
export async function loadReferencesData(company: string): Promise<ReferencesData> {
  const d = needDb();
  const [r, c] = await Promise.all([
    getDocs(collection(d, "companies", company, "references")),
    getDoc(doc(d, "companies", company, "settings", "referenceCopy")),
  ]);
  const str = (v: unknown) => (v === null || v === undefined ? "" : String(v));
  const refs: Reference[] = r.docs.map((x) => ({
    refId: x.id,
    kind: x.get("kind") as Reference["kind"],
    title: str(x.get("title")),
    number: str(x.get("number")),
    edition: str(x.get("edition")),
    covers: str(x.get("covers")),
    notes: str(x.get("notes")),
    status: x.get("status") === "Superseded" ? "Superseded" : "Current",
    replacedBy: str(x.get("replacedBy")),
    replaces: str(x.get("replaces")),
    fileName: str(x.get("fileName")),
    contentType: str(x.get("contentType")),
    sizeBytes: Number(x.get("sizeBytes") ?? 0),
    storagePath: str(x.get("storagePath")),
    hasText: typeof x.get("hasText") === "boolean" ? (x.get("hasText") as boolean) : null,
    addedOn: str(x.get("addedOn")),
    addedByName: str(x.get("addedByName")),
    history: ((x.get("history") as HistoryEntry[] | undefined) ?? []).filter(Boolean),
  }));
  const cd = c.exists() ? c : null;
  const copy = cd ? { lastCopiedAt: str(cd.get("lastCopiedAt")), lastResult: str(cd.get("lastResult")), lastError: str(cd.get("lastError")) } : null;
  return { refs, copy };
}

function needStorage() {
  if (!app) throw new Error("Firebase not started");
  if (!app.options.storageBucket) throw new Error("File storage is not switched on for this project yet. Firebase console > Storage > Get Started.");
  return getStorage(app);
}

/** Uploads to references/incoming (storage rule 1). Returns the upload name for saveReference. */
export async function uploadReferenceFile(company: string, file: File): Promise<string> {
  const name = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeFileName(file.name)}`;
  await uploadBytes(storageRef(needStorage(), incomingPath(company, name)), file, { contentType: file.type });
  return name;
}

/** A link to open a library file (storage rule 2). Admin only. */
export async function referenceFileUrl(storagePath: string): Promise<string> {
  return getDownloadURL(storageRef(needStorage(), storagePath));
}

// ---- Gloves roster (GLOVES_WORDS.md, GLOVES_REWORK_WORDS.md) ----

export interface GlovesData extends PeopleData {
  gloves: GloveDoc[];
  inspections: InspectionDoc[];
}

/** People and badges (as People & Badges reads them) plus every pair and inspection (rule 19). */
export async function loadGlovesData(company: string): Promise<GlovesData> {
  const d = needDb();
  const [people, g, i] = await Promise.all([
    loadPeopleData(company),
    getDocs(collection(d, "companies", company, "gloves")),
    getDocs(collection(d, "companies", company, "gloveInspections")),
  ]);
  return {
    ...people,
    gloves: g.docs.map((x) => ({ ...x.data(), pairId: x.id })),
    inspections: i.docs.map((x) => ({ ...(x.data() as InspectionDoc), inspectedAt: String(x.get("inspectedAt") ?? "") })),
  };
}

// ---- Email Log (EMAIL_SENDER_WORDS.md, 2026-10-10) ----

export interface EmailData {
  rows: EmailRow[];
  /** Site number to name, for the site select and headings. */
  siteNames: Record<string, string>;
}

/** The newest 300 emails by createdAt (rule 24), filtered on the page. One field, so no composite index. */
export async function loadEmailData(company: string): Promise<EmailData> {
  const d = needDb();
  const [logSnap, siteSnap] = await Promise.all([
    getDocs(query(collection(d, "companies", company, "emailLog"), orderBy("createdAt", "desc"), limit(LOG_LIMIT))),
    getDocs(collection(d, "companies", company, "sites")),
  ]);
  const siteNames: Record<string, string> = {};
  for (const x of siteSnap.docs) siteNames[x.id] = String(x.get("name") ?? `Site ${x.id}`);
  return { rows: logSnap.docs.map((x) => emailRowOf(x.id, x.data())), siteNames };
}

// ---- Work Requests (WORK_REQUEST_WORDS.md, rule 25) ----

export interface WorkRequestData {
  rows: WorkRequestRow[];
  siteNames: Record<string, string>;
}

/** Every request in the company, newest first. The page filters by site and status. */
export async function loadWorkRequests(company: string): Promise<WorkRequestData> {
  const d = needDb();
  const [reqSnap, siteSnap] = await Promise.all([
    getDocs(query(collection(d, "companies", company, "workRequests"), orderBy("createdAt", "desc"), limit(500))),
    getDocs(collection(d, "companies", company, "sites")),
  ]);
  const siteNames: Record<string, string> = {};
  for (const x of siteSnap.docs) siteNames[x.id] = String(x.get("name") ?? `Site ${x.id}`);
  return { rows: reqSnap.docs.map((x) => requestRowOf(x.id, x.data())), siteNames };
}

/**
 * Calls a server function by name (functions/src). The server checks every
 * rule; the page only shows what comes back. Throws the server's message.
 */
export async function callServer<T>(name: string, data: Record<string, unknown>): Promise<T> {
  const fns = fnsBySlot[active];
  if (!fns) throw new Error("Firebase not started");
  try {
    const res = await httpsCallable<Record<string, unknown>, T>(fns, name)(data);
    return res.data;
  } catch (e) {
    const err = e as { code?: string; message?: string; details?: unknown };
    const network = ["functions/internal", "functions/unavailable", "functions/deadline-exceeded"].includes(err.code ?? "");
    throw new ServerError(
      // The SDK can tack the HTTP status on the end, "[400]". The person does not need it.
      network || !err.message ? "Could not reach SafeTag. Check your signal and try again." : err.message.replace(/\s*\[\d{3}\]$/, ""),
      (err.code ?? "").replace(/^functions\//, ""),
      err.details,
    );
  }
}

/** A refusal from server code. The message is written for the person on the page. */
export class ServerError extends Error {
  constructor(
    message: string,
    readonly code: string,
    readonly details: unknown,
  ) {
    super(message);
  }
}

// ---- Near Miss and Contractor Visits (piece 6, rules 27 and 28) ----

export interface NearMissData {
  rows: NearMissRow[];
  siteNames: Record<string, string>;
}

/** Every near miss in the company, newest first. The page filters by site and status. */
export async function loadNearMisses(company: string): Promise<NearMissData> {
  const d = needDb();
  const [snap, siteSnap] = await Promise.all([
    getDocs(query(collection(d, "companies", company, "observations"), orderBy("createdAt", "desc"), limit(500))),
    getDocs(collection(d, "companies", company, "sites")),
  ]);
  const siteNames: Record<string, string> = {};
  for (const x of siteSnap.docs) siteNames[x.id] = String(x.get("name") ?? `Site ${x.id}`);
  return { rows: snap.docs.map((x) => nearMissRowOf(x.id, x.data())), siteNames };
}

export interface VisitData {
  rows: VisitRow[];
  siteNames: Record<string, string>;
}

/** Every contractor visit in the company, newest first. */
export async function loadContractorVisits(company: string): Promise<VisitData> {
  const d = needDb();
  const [snap, siteSnap] = await Promise.all([
    getDocs(query(collection(d, "companies", company, "contractorVisits"), orderBy("createdAt", "desc"), limit(500))),
    getDocs(collection(d, "companies", company, "sites")),
  ]);
  const siteNames: Record<string, string> = {};
  for (const x of siteSnap.docs) siteNames[x.id] = String(x.get("name") ?? `Site ${x.id}`);
  return { rows: snap.docs.map((x) => visitRowOf(x.id, x.data())), siteNames };
}

// ---- IEA Site Survey (IEA_OFFLINE_SHELL_WORDS.md rev 3) ----
// The site pack is the survey's offline copy of what it reads. Download reads from the server,
// which also fills the on-device store. Opening offline reads that store back.

export interface SurveySite {
  siteNumber: string;
  name: string;
  /** The site's Drive folder, written by server code (SITE_FOLDERS_WORDS.md). */
  driveFolder?: { id: string; url: string; rootFolderId?: string } | null;
}

/** Sites the badge covers, by name. From the server with signal, from the device without. */
export async function loadSurveySites(company: string, covered: string[]): Promise<SurveySite[]> {
  const ref = collection(needDb(), "companies", company, "sites");
  const snap = await (navigator.onLine === false ? getDocsFromCache(ref) : getDocs(ref));
  return snap.docs
    .map((x) => {
      const d = x.data() as { name?: string; driveFolder?: SurveySite["driveFolder"] };
      return { siteNumber: x.id, name: String(d.name ?? x.id), driveFolder: d.driveFolder ?? null };
    })
    .filter((s) => covered.includes("ALL") || covered.includes(s.siteNumber))
    .sort((a, b) => a.siteNumber.localeCompare(b.siteNumber));
}

export function packAssetOf(id: string, d: Record<string, unknown>): PackAsset {
  return {
    assetId: id,
    location: String(d.location ?? ""),
    equipmentType: String(d.equipmentType ?? ""),
    equipmentCode: String(d.equipmentCode ?? ""),
    nominalVoltage: String(d.nominalVoltage ?? ""),
    nominalVolts: typeof d.nominalVolts === "number" ? d.nominalVolts : null,
    isOutOfService: d.isOutOfService === true,
  };
}

/** Reads one site's pack. fromServer: the download. Otherwise the copy on this device. */
export async function readSitePack(company: string, site: SurveySite, fromServer: boolean): Promise<SitePack> {
  const d = needDb();
  const read = fromServer ? getDocsFromServer : getDocsFromCache;
  const base = ["companies", company, "sites", site.siteNumber] as const;
  const [assets, iea, links, classes, fields, tags, drawing] = await Promise.all([
    read(collection(d, ...base, "assets")),
    // An admin without the IEA Site Survey switch opens the Asset ID Creator from the same pack.
    // The survey parts that badge cannot read come back empty instead of failing the pack.
    read(collection(d, ...base, "ieaData")).catch(() => null),
    read(collection(d, ...base, "connections")).catch(() => null),
    read(collection(d, "companies", company, "lists", "equipmentClasses", "items")),
    // Collect's dropdowns. An older device copy without them falls back to the approved lists.
    read(collection(d, "companies", company, "lists", "fieldLists", "items")).catch(() => null),
    // Model Tags (AF_MAPPING_WORDS.md section 5). An older device copy without them starts from none.
    read(collection(d, ...base, "modelTags")).catch(() => null),
    // The Drawing's hand-moved labels (section H). An older device copy without them lays everything out on its own.
    read(collection(d, ...base, "drawing")).catch(() => null),
  ]);
  const ieaRecords: Record<string, Record<string, unknown>> = {};
  for (const x of iea?.docs ?? []) {
    const v = x.data() as Record<string, unknown>;
    const id = String(v["Asset ID"] ?? "");
    if (id) ieaRecords[id] = { ...(ieaRecords[id] ?? {}), ...v };
  }
  const ieeeClassOf: Record<string, string> = {};
  for (const c of classes.docs) ieeeClassOf[c.id] = String((c.data() as { ieeeClass?: string }).ieeeClass ?? "");
  return {
    siteNumber: site.siteNumber,
    siteName: site.name,
    savedAt: "",
    assets: assets.docs
      .filter((x) => (x.data() as { isRemoved?: boolean }).isRemoved !== true)
      .map((x) => packAssetOf(x.id, x.data()))
      .sort((a, b) => a.assetId.localeCompare(b.assetId, undefined, { numeric: true })),
    ieaDone: [...new Set((iea?.docs ?? []).map((x) => String((x.data() as Record<string, unknown>)["Asset ID"] ?? "")).filter(Boolean))],
    links: (links?.docs ?? []).map((x) => {
      const v = x.data() as Record<string, unknown>;
      const g = (k: string) => String(v[k] ?? "");
      return {
        fromAsset: g("From Asset"),
        toAsset: g("To Asset"),
        goesTo: g("Goes To"),
        status: String(v.Status ?? "Active"),
        id: x.id,
        fromPoint: g("From Point"),
        toPoint: g("To Point"),
        howKnown: g("How Known"),
        endLoadType: g("End Load Type"),
        endLoadHp: g("End Load HP"),
        endLoadTag: g("End Load Tag"),
        notes: g("Notes"),
        recordedAt: g("Recorded At"),
        row: v,
      };
    }),
    ieeeClassOf,
    ieaRecords,
    assetDocs: Object.fromEntries(assets.docs.map((x) => [x.id, x.data() as Record<string, unknown>])),
    classes: classes.docs.map((c) => {
      const v = c.data() as { code?: string; name?: string; order?: number; isActive?: boolean };
      return { code: String(v.code ?? c.id), name: String(v.name ?? ""), order: typeof v.order === "number" ? v.order : null, isActive: v.isActive !== false };
    }),
    modelTags: (tags?.docs ?? []).map((x) => x.data() as Record<string, unknown>),
    drawing: (drawing?.docs ?? []).map((x) => x.data() as Record<string, unknown>),
    fieldLists: (fields?.docs ?? []).map((x) => {
      const v = x.data() as Record<string, unknown>;
      return { listName: String(v.listName ?? ""), value: String(v.value ?? ""), group: String(v.group ?? ""), order: typeof v.order === "number" ? v.order : null, isActive: v.isActive !== false };
    }),
  };
}

// ---- IEA Site Survey sends (IEA_OFFLINE_SHELL_WORDS.md sections 10 to 13) ----

/**
 * Gives up on a read that never answers. A stuck local copy (another SafeTag window holding it)
 * left pages on "Loading…" for good (2026-10-22). Now the page shows the reason and Try Again.
 */
export function withTimeLimit<T>(p: Promise<T>, ms = 20_000): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`No answer from the cloud in ${Math.round(ms / 1000)} seconds. Close any other SafeTag windows, then tap Try Again.`)), ms);
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e: unknown) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });
}

/** The error code Firebase gave, without its prefix: "permission-denied", "unauthorized". */
export function errorCode(e: unknown): string {
  const c = (e as { code?: string })?.code ?? "";
  return c.replace(/^(firestore|storage)\//, "");
}

/**
 * Sends one survey record at its fixed path. Resolves only when the cloud confirms.
 * With no signal Firestore keeps it on the device and sends it when signal returns.
 */
export async function sendSurveyRecord(path: string, data: Record<string, unknown>): Promise<void> {
  await setDoc(doc(needDb(), path), data, { merge: true });
}

/** One asset as the cloud holds it, or null when there is none. Needs signal. The Asset ID page uses it to tell a taken ID. */
export async function cloudAsset(company: string, site: string, assetId: string): Promise<Record<string, unknown> | null> {
  const snap = await getDocFromServer(doc(needDb(), "companies", company, "sites", site, "assets", assetId));
  return snap.exists() ? (snap.data() as Record<string, unknown>) : null;
}

/** Recorded At on the cloud copy of a record, or null when there is none. Needs signal. */
export async function cloudRecordedAt(path: string): Promise<string | null> {
  const snap = await getDocFromServer(doc(needDb(), path));
  if (!snap.exists()) return null;
  const v = (snap.data() as Record<string, unknown>)["Recorded At"];
  return typeof v === "string" ? v : "";
}

/** Uploads one survey photo. Private: storage rule 3. */
/** A link to show an IEA photo (storage rule 3: admins read). */
export async function surveyPhotoUrl(path: string): Promise<string> {
  return getDownloadURL(storageRef(needStorage(), path));
}

export async function uploadSurveyPhoto(path: string, blob: Blob, contentType: string): Promise<void> {
  await uploadBytes(storageRef(needStorage(), path), blob, { contentType });
}

const walkStatusPath = (company: string, site: string) => doc(needDb(), "companies", company, "sites", site, "walkStatus", "current");

/** Survey in progress for one site. From the server with signal, from the device without. */
export async function readWalkStatus(company: string, site: string): Promise<WalkStatusDoc | null> {
  const ref = walkStatusPath(company, site);
  const snap = await (navigator.onLine === false ? getDocFromCache(ref) : getDoc(ref)).catch(() => null);
  return snap && snap.exists() ? (snap.data() as WalkStatusDoc) : null;
}

/**
 * One Model Tag as the cloud holds it, read fresh. Null when the cloud has none or cannot be reached.
 * Used to tell a number another iPad took first from a refusal for any other reason.
 */
export async function readModelTag(company: string, site: string, tag: string): Promise<Record<string, unknown> | null> {
  const snap = await getDocFromServer(doc(needDb(), "companies", company, "sites", site, "modelTags", tag)).catch(() => null);
  return snap && snap.exists() ? (snap.data() as Record<string, unknown>) : null;
}

/** Writes survey in progress. Not awaited by the page: it sends when it can. */
export function writeWalkStatus(company: string, site: string, status: WalkStatusDoc): Promise<void> {
  return setDoc(walkStatusPath(company, site), status);
}

// ---- Arc Flash Labels (AF_LABEL_IMPORT_WORDS.md, mockup approved 2026-10-21) ----
// Reads only (rules 29 and 30). Import, Review changes and Publish go through server code.

export interface AfStudySummary {
  studyId: string;
  siteNumber: string;
  path: "Model Tag" | "Legacy";
  state: "Imported" | "Published" | "Labels Created" | "Replaced";
  firm: string;
  ieaBy: string;
  jobNumber: string;
  assessmentDate: string;
  engineer: string;
  method: string;
  counts: ReturnType<typeof import("./af-study").countsOf>;
  readingCount: number;
  busCount: number;
  summaryCount: number;
  deviceCount: number;
  utility: { bus: string; readings: import("./af-study").Reading[] }[];
  settingChanges: { device: string; field: string; asFound: string; recommended: string }[];
  files: { name: string; storagePath: string; size: number; contentType: string }[];
  importedAt: string;
  importedByName: string;
  publishCheck?: { checked: number; allGood: boolean; misses: string[]; at: string; byName: string };
  replacedBy?: string;
  /** Every Create Labels set, oldest first. Kept for good. */
  labelSets?: { setId: string; at: string; byName: string; files: { name: string; storagePath: string }[]; counts: { warning: number; danger: number; skipped: number; flagged: number } }[];
}

/** The sites this admin covers, by number. */
export async function loadAfSites(company: string, covers: readonly string[]): Promise<{ siteNumber: string; name: string }[]> {
  const snap = await getDocs(collection(needDb(), "companies", company, "sites"));
  return snap.docs
    .map((x) => ({ siteNumber: x.id, name: String(x.get("name") ?? `Site ${x.id}`) }))
    .filter((s) => covers.includes("ALL") || covers.includes(s.siteNumber))
    .sort((a, b) => a.siteNumber.localeCompare(b.siteNumber));
}

/** The site's studies, newest first. Admin covering the site (rule 29). */
export async function loadAfStudies(company: string, site: string): Promise<AfStudySummary[]> {
  const snap = await getDocs(collection(needDb(), "companies", company, "sites", site, "afStudies"));
  return snap.docs.map((x) => ({ ...(x.data() as AfStudySummary), studyId: x.id })).sort((a, b) => b.importedAt.localeCompare(a.importedAt));
}

// ---- Field Labels walk (rule 36, COHESION_WORDS.md): on the iPad first, then the Outbox sends it ----

export interface FieldWalkDoc {
  siteNumber: string;
  walkJson: string;
  roomsTotal: number;
  roomsDone: number;
  gearTotal: number;
  savedAt: string;
  badgeId: string;
  holderName: string;
  deviceId?: string;
  deviceName?: string;
}

export const fieldWalkPath = (company: string, site: string): string => `companies/${company}/sites/${site}/fieldLabels/walk`;

/** The walk the cloud holds, or null when there is none. Needs signal. */
export async function loadFieldWalk(company: string, site: string): Promise<FieldWalkDoc | null> {
  const snap = await getDocFromServer(doc(needDb(), fieldWalkPath(company, site)));
  return snap.exists() ? (snap.data() as FieldWalkDoc) : null;
}

/**
 * One site's program for the Site Hub (COHESION_WORDS.md Move 1): the same reads the survey's pack makes,
 * plus the arc flash studies. From the server. Nothing is saved on the device.
 */
export async function loadSiteProgram(company: string, site: SurveySite, canWalk: boolean): Promise<import("./site-hub").ProgramData> {
  const [pack, studies, walk] = await Promise.all([
    readSitePack(company, site, true),
    loadAfStudies(company, site.siteNumber).catch(() => []),
    loadFieldWalk(company, site.siteNumber).catch(() => null),
  ]);
  return { assetDocs: pack.assetDocs ?? {}, assets: pack.assets, links: pack.links, ieaDone: canWalk ? pack.ieaDone : null, studies, fieldWalk: walk };
}

/** One study's label rows. */
export async function loadAfRows(company: string, site: string, studyId: string): Promise<import("./af-study").StoredRow[]> {
  const snap = await getDocs(collection(needDb(), "companies", company, "sites", site, "afStudies", studyId, "rows"));
  return snap.docs.map((x) => x.data() as import("./af-study").StoredRow);
}

/** The site's registry, for the Asset ID and Isolate At choices in Review. Covered site (rules 2 and 3). */
/** What printed for each Asset ID at the site (rule 37), by Asset ID. */
export async function loadAfLabelPrints(company: string, site: string): Promise<Record<string, import("./af-label-print").AfPrintRecord>> {
  const snap = await getDocs(collection(needDb(), "companies", company, "sites", site, "afLabelPrints"));
  const out: Record<string, import("./af-label-print").AfPrintRecord> = {};
  for (const x of snap.docs) {
    const d = x.data();
    out[x.id] = { assetId: x.id, stock: d.stock === "DANGER" ? "DANGER" : "WARNING", values: String(d.values ?? ""), at: String(d.at ?? ""), studyId: String(d.studyId ?? ""), setId: String(d.setId ?? "") };
  }
  return out;
}

export async function loadAfRegistry(company: string, site: string): Promise<import("./af-study").RegistryAsset[]> {
  const snap = await getDocs(collection(needDb(), "companies", company, "sites", site, "assets"));
  return snap.docs
    .filter((x) => x.get("isRemoved") !== true)
    .map((x) => ({
      assetId: x.id,
      equipmentType: String(x.get("equipmentType") ?? ""),
      nominalVolts: typeof x.get("nominalVolts") === "number" ? (x.get("nominalVolts") as number) : null,
      limitedApproach: String(x.get("limitedApproach") ?? ""),
      conductorType: String(x.get("conductorType") ?? ""),
    }));
}

/** Uploads one of KE's files to the site's incoming folder (storage rule 4). Returns the upload name. */
export async function uploadAfStudyFile(company: string, site: string, file: File): Promise<string> {
  const name = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${safeFileName(file.name)}`;
  const type = file.type || (/\.xls$/i.test(file.name) ? "application/vnd.ms-excel" : /\.xlsx$/i.test(file.name) ? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" : /\.csv$/i.test(file.name) ? "text/csv" : "application/pdf");
  await uploadBytes(storageRef(needStorage(), `companies/${company}/sites/${site}/afStudyFiles/incoming/${name}`), file, { contentType: type });
  return name;
}

/** A link to open one of a study's files (storage rule 5). */
export async function afStudyFileUrl(storagePath: string): Promise<string> {
  return getDownloadURL(storageRef(needStorage(), storagePath));
}

/** Saves one file of a label set under the study (storage rule 6). Returns its storage path. */
export async function uploadAfLabelFile(company: string, site: string, studyId: string, setId: string, name: string, bytes: Uint8Array | string): Promise<string> {
  const path = `companies/${company}/sites/${site}/afStudyFiles/${studyId}/labels/${setId}/${name}`;
  const type = name.endsWith(".csv") ? "text/csv" : "application/pdf";
  const data = typeof bytes === "string" ? new TextEncoder().encode(bytes) : bytes;
  await uploadBytes(storageRef(needStorage(), path), data, { contentType: type });
  return path;
}

// ---- Google Drive (SITE_FOLDERS_WORDS.md rev 3.1, MOCKUP_DRIVE_FOLDERS.html rev 2, rules 33 to 35, storage 7) ----

/** settings/drive (rule 33). Null when Drive was never set up, or the read is refused. Cache first offline. */
export async function loadDriveSettings(company: string): Promise<Partial<import("./drive-folders").DriveSettings> | null> {
  try {
    return await readDriveSettings(company);
  } catch {
    return null;
  }
}

/** The same read, for the Google Drive page: a failed read throws, so the page can offer Try Again. */
export async function readDriveSettings(company: string): Promise<Partial<import("./drive-folders").DriveSettings> | null> {
  const ref = doc(needDb(), "companies", company, "settings", "drive");
  const snap = await (navigator.onLine === false ? getDocFromCache(ref) : getDoc(ref));
  return snap.exists() ? (snap.data() as Partial<import("./drive-folders").DriveSettings>) : null;
}

/** The site record as it changes: the server writes driveFolder when Build Folder finishes. */
export function watchSiteDriveFolder(company: string, site: string, cb: (folder: SurveySite["driveFolder"]) => void): () => void {
  return onSnapshot(
    doc(needDb(), "companies", company, "sites", site),
    (snap) => cb(((snap.data() ?? {}) as { driveFolder?: SurveySite["driveFolder"] }).driveFolder ?? null),
    () => undefined,
  );
}

/** One Build Folder ask as the server marks it (rule 34). */
export function watchDriveRequest(company: string, site: string, requestId: string, cb: (state: string, reason: string) => void): () => void {
  return onSnapshot(
    doc(needDb(), "companies", company, "sites", site, "driveRequests", requestId),
    (snap) => cb(String(snap.get("state") ?? ""), String(snap.get("reason") ?? "")),
    () => undefined,
  );
}

/** One label PDF's Saved to Drive record (rule 35), as the server writes it. */
export function watchDriveFile(
  company: string,
  site: string,
  fileName: string,
  cb: (r: { state: string; reason: string; folderPath: string; folderUrl: string; url?: string; savedAt?: string } | null) => void,
): () => void {
  return onSnapshot(
    doc(needDb(), "companies", company, "sites", site, "driveFiles", fileName),
    (snap) =>
      cb(
        snap.exists()
          ? { state: String(snap.get("state") ?? ""), reason: String(snap.get("reason") ?? ""), folderPath: String(snap.get("folderPath") ?? ""), folderUrl: String(snap.get("folderUrl") ?? ""), url: String(snap.get("url") ?? ""), savedAt: String(snap.get("savedAt") ?? "") }
          : null,
      ),
    () => cb(null),
  );
}

/** Names already used in the outbox today, so a second print gets _2 (storage 7 refuses an overwrite). */
export async function driveFileNamesTaken(company: string, site: string): Promise<string[]> {
  try {
    const snap = await getDocs(collection(needDb(), "companies", company, "sites", site, "driveFiles"));
    return snap.docs.map((x) => x.id);
  } catch {
    return [];
  }
}

/**
 * The label master PDFs in Drive (list 2, 2026-10-25): the latest send per Drive name, saved or not.
 * Each Update PDF leaves one driveFiles record, so the latest one says where the file stands.
 */
export async function loadDriveMasters(company: string, site: string): Promise<Map<string, import("./print-card").MasterRecord>> {
  const snap = await getDocs(collection(needDb(), "companies", company, "sites", site, "driveFiles"));
  const out = new Map<string, import("./print-card").MasterRecord>();
  for (const d of snap.docs) {
    const v = d.data();
    if (v.master !== true || v.older === true) continue;
    const name = String(v.driveName ?? "");
    const rec = {
      driveName: name,
      state: (["saved", "failed", "retrying"].includes(String(v.state)) ? v.state : "waiting") as "saved" | "failed" | "retrying" | "waiting",
      savedAt: String(v.savedAt ?? ""),
      url: String(v.url ?? ""),
      folderPath: String(v.folderPath ?? ""),
      folderUrl: String(v.folderUrl ?? ""),
      reason: String(v.reason ?? ""),
      sendName: d.id,
    };
    const had = out.get(name);
    // The send's own stamp orders them: the name ends __<ms>.pdf.
    if (!had || had.sendName < rec.sendName) out.set(name, rec);
  }
  return out;
}

/** Uploads one label PDF to the Drive outbox (storage 7), stamped with the badge. The server copies it to Drive. */
export async function uploadDrivePdf(path: string, blob: Blob, meta: Record<string, string>, contentType = "application/pdf"): Promise<void> {
  await uploadBytes(storageRef(needStorage(), path), blob, { contentType, customMetadata: meta });
}

/**
 * The site's Mark Complete checks (site-stage.ts marksOf), for the Push To Assessment gate on Field Labels
 * (ASSESSMENT_FLOW_B5_WORDS.md). With signal from the server, without it from this device. Null: not read.
 */
export async function loadStepsDone(company: string, site: string): Promise<Record<string, unknown> | null> {
  const ref = doc(needDb(), "companies", company, "sites", site);
  const snap = await (navigator.onLine === false ? getDocFromCache(ref) : getDocFromServer(ref));
  return snap.exists() ? ((snap.get("stepsDone") as Record<string, unknown> | undefined) ?? {}) : null;
}

/** A Field Labels photo another iPad took: its link from the outbox (storage rule 7 lets a site admin read it). */
export function walkPhotoUrl(path: string): Promise<string> {
  return getDownloadURL(storageRef(needStorage(), path));
}

// ---- One asset page (ONE_ASSET_PAGE_WORDS.md, approved 2026-10-24) ----

/** Everything the asset page reads past the site's asset list. Survey parts stay undefined without the switch. */
export async function loadAssetPage(
  company: string,
  site: string,
  assetId: string,
  canWalk: boolean,
): Promise<Pick<import("./asset-page").AssetPageData, "doc" | "changes" | "iea" | "links" | "afLabel" | "study"> | null> {
  const d = needDb();
  const base = ["companies", company, "sites", site] as const;
  const [asset, changes, label, iea, from, to] = await Promise.all([
    getDoc(doc(d, ...base, "assets", assetId)),
    getDocs(query(collection(d, ...base, "assetChanges"), where("Asset ID", "==", assetId))).catch(() => null),
    getDoc(doc(d, ...base, "afLabels", assetId)).catch(() => null),
    canWalk ? getDoc(doc(d, ...base, "ieaData", assetId)).catch(() => null) : Promise.resolve(undefined),
    canWalk ? getDocs(query(collection(d, ...base, "connections"), where("From Asset", "==", assetId))).catch(() => null) : Promise.resolve(undefined),
    canWalk ? getDocs(query(collection(d, ...base, "connections"), where("To Asset", "==", assetId))).catch(() => null) : Promise.resolve(undefined),
  ]);
  if (!asset.exists()) return null;
  const data = asset.data() as Record<string, unknown>;
  const studyId = typeof data.afStudyId === "string" ? data.afStudyId : "";
  const st = studyId ? await getDoc(doc(d, ...base, "afStudies", studyId)).catch(() => null) : null;
  const sd = st?.exists() ? (st.data() as Record<string, unknown>) : null;
  const sets = sd && Array.isArray(sd.labelSets) ? (sd.labelSets as { at?: string }[]) : [];
  const linkOf = (x: { data: () => unknown }) => {
    const v = x.data() as Record<string, unknown>;
    return { from: String(v["From Asset"] ?? ""), to: String(v["To Asset"] ?? ""), status: String(v.Status ?? "Active") };
  };
  return {
    doc: data,
    changes: changes ? changes.docs.map((x) => x.data() as Record<string, unknown>) : [],
    afLabel: label?.exists() ? (label.data() as Record<string, unknown>) : null,
    iea: iea === undefined ? undefined : iea?.exists() ? (iea.data() as Record<string, unknown>) : null,
    links: from === undefined || to === undefined ? undefined : [...(from?.docs ?? []), ...(to?.docs ?? [])].map(linkOf),
    study: sd
      ? {
          studyId,
          firm: String(sd.firm ?? ""),
          jobNumber: String(sd.jobNumber ?? ""),
          assessmentDate: String(sd.assessmentDate ?? ""),
          state: String(sd.state ?? ""),
          labelsAt: String(sets[sets.length - 1]?.at ?? ""),
        }
      : null,
  };
}
